// ============================================================
// FilterStrip.jsx — preset + favourite + custom filter thumbnails
// ------------------------------------------------------------
// Sections (top → bottom): ★ Favourites · Filters (presets) · My Filters
// (custom). Each item lazily renders a 60×60 preview of the source photo,
// has a star toggle (max 5 favourites), and custom items have a trash icon.
// A strength slider appears for preset selections.
// ============================================================

import React, { useEffect, useRef, useState } from 'react';
import { PRESET_FILTERS, PRESET_NAMES } from '../../utils/filterEngine';
import { loadImage, renderThumbnail } from '../../utils/canvasExport';

function FilterItem({ thumb, name, active, isFav, onClick, onToggleFav, onDelete }) {
  return (
    <div className={`pe-filter-item${active ? ' active' : ''}`}>
      <button type="button" className="pe-filter-btn" onClick={onClick} title={name}>
        <span className="pe-filter-thumb">
          {thumb ? <img src={thumb} alt={name} /> : <span className="pe-filter-thumb-ph" />}
        </span>
        <span className="pe-filter-name">{name}</span>
      </button>
      <button
        type="button"
        className={`pe-star${isFav ? ' on' : ''}`}
        onClick={(e) => { e.stopPropagation(); onToggleFav(); }}
        title={isFav ? 'Remove favourite' : 'Add favourite'}
      >★</button>
      {onDelete && (
        <button
          type="button"
          className="pe-trash"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          title="Delete filter"
        >🗑</button>
      )}
    </div>
  );
}

export default function FilterStrip({
  photo,
  activeFilter,
  filterStrength,
  onSelect,
  onStrength,
  customFilters = [],
  favouriteFilters = [],
  onSelectCustom,
  onToggleFav,
  onDeleteCustom,
  onSaveCurrent,
}) {
  const [thumbs, setThumbs] = useState({}); // key → dataUrl
  const imgRef = useRef(null);
  const [imgReady, setImgReady] = useState(false);

  // Load source + render preset thumbnails (staggered).
  useEffect(() => {
    let cancelled = false;
    const timers = [];
    setThumbs({});
    setImgReady(false);
    imgRef.current = null;
    if (!photo?.url) return;

    loadImage(photo.url).then((img) => {
      if (cancelled) return;
      imgRef.current = img;
      setImgReady(true);
      PRESET_NAMES.forEach((name, i) => {
        const t = setTimeout(() => {
          if (cancelled || !imgRef.current) return;
          try {
            const url = renderThumbnail(imgRef.current, PRESET_FILTERS[name], 60);
            setThumbs((prev) => ({ ...prev, [name]: url }));
          } catch (_) { /* ignore */ }
        }, i * 16);
        timers.push(t);
      });
    }).catch(() => {});

    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [photo?.url]);

  // Render custom-filter thumbnails when the list (or image) changes.
  useEffect(() => {
    if (!imgReady || !imgRef.current) return;
    customFilters.forEach((cf) => {
      const key = `custom:${cf.id}`;
      setThumbs((prev) => {
        if (prev[key]) return prev;
        try {
          const url = renderThumbnail(imgRef.current, cf.settings, 60);
          return { ...prev, [key]: url };
        } catch (_) { return prev; }
      });
    });
  }, [imgReady, customFilters]);

  const isFav = (id, type) => favouriteFilters.some((f) => f.id === id && f.type === type);

  // Resolve favourites to renderable items (skip deleted customs).
  const favItems = favouriteFilters.map((f) => {
    if (f.type === 'preset' && PRESET_FILTERS[f.id]) {
      return { key: f.id, name: f.id, thumb: thumbs[f.id], active: activeFilter === f.id,
        onClick: () => onSelect(f.id), ref: f };
    }
    if (f.type === 'custom') {
      const cf = customFilters.find((c) => c.id === f.id);
      if (!cf) return null;
      return { key: `custom:${cf.id}`, name: cf.name, thumb: thumbs[`custom:${cf.id}`],
        active: activeFilter === `custom:${cf.id}`, onClick: () => onSelectCustom(cf), ref: f };
    }
    return null;
  }).filter(Boolean);

  const isPresetActive = activeFilter !== 'Normal' && !activeFilter.startsWith('custom:');

  return (
    <div className="pe-filterstrip">
      {favItems.length > 0 && (
        <>
          <div className="pe-strip-head">★ Favourites</div>
          <div className="pe-strip-scroll">
            {favItems.map((it) => (
              <FilterItem
                key={it.key} thumb={it.thumb} name={it.name} active={it.active}
                isFav onClick={it.onClick} onToggleFav={() => onToggleFav(it.ref)}
              />
            ))}
          </div>
        </>
      )}

      <div className="pe-strip-head">Filters</div>
      <div className="pe-strip-grid">
        {PRESET_NAMES.map((name) => (
          <FilterItem
            key={name} thumb={thumbs[name]} name={name}
            active={activeFilter === name}
            isFav={isFav(name, 'preset')}
            onClick={() => onSelect(name)}
            onToggleFav={() => onToggleFav({ id: name, type: 'preset', name })}
          />
        ))}
      </div>

      <div className="pe-strip-head pe-strip-head-row">
        <span>My Filters</span>
        <button className="pe-link-btn" onClick={onSaveCurrent}>＋ Save current</button>
      </div>
      {customFilters.length === 0 ? (
        <div className="pe-strip-empty">Save the current edit to reuse it later.</div>
      ) : (
        <div className="pe-strip-grid">
          {customFilters.map((cf) => (
            <FilterItem
              key={cf.id} thumb={thumbs[`custom:${cf.id}`]} name={cf.name}
              active={activeFilter === `custom:${cf.id}`}
              isFav={isFav(cf.id, 'custom')}
              onClick={() => onSelectCustom(cf)}
              onToggleFav={() => onToggleFav({ id: cf.id, type: 'custom', name: cf.name })}
              onDelete={() => onDeleteCustom(cf.id)}
            />
          ))}
        </div>
      )}

      {isPresetActive && (
        <div className="pe-strength">
          <span className="pe-strength-label">Strength</span>
          <input
            type="range" min={0} max={100} value={filterStrength}
            onChange={(e) => onStrength(Number(e.target.value))} className="pe-range"
          />
          <span className="pe-strength-val">{filterStrength}%</span>
        </div>
      )}
    </div>
  );
}
